import chroma from 'chroma-js';
import clsx from 'clsx';
import {
  createElement,
  CSSProperties,
  forwardRef,
  HTMLProps,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import { ThemeColors } from '../../theme';
import { isColorPalette } from '../../utils';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Typography } from '../typography';

import classes from './tag.module.scss';

export type TagProps = Omit<
  HTMLProps<HTMLButtonElement | HTMLDivElement>,
  | 'color'
  | 'ref'
  | 'prefix'
  | 'suffix'
  | 'type'
  | 'children'
  | 'onClick'
  | 'onChange'
  | 'value'
>
  & PropsWithChildren & {
    /**
       * Custom prefix item
       */
    prefix?: ReactNode;

    /**
       * Custom suffix item
       */
    suffix?: ReactNode;

    /**
       * Custom color palette. Overwrites the background and foreground properties
       */
    color?: ThemeColors | string;

    /**
       * Renders in active state
       */
    active?: boolean;

    /**
       * Disables input
       */
    disabled?: boolean;

    /**
       * Custom click handler
       */
    onClick?: MouseEventHandler<HTMLButtonElement>;

    /**
       * Custom value used for selections
       */
    value?: number;

    /**
       * A callback function that is triggered when a removal action occurs.
       * Typically used to handle cleanup or specific operations after an item is removed.
       *
       * @type {function|undefined}
       */
    onRemove?: () => void;
  };

export const Tag = forwardRef<HTMLButtonElement, TagProps>(
  (
    {
      active,
      color = 'grey',
      children,
      prefix,
      suffix,
      disabled,
      onClick,
      onRemove,
      ...props
    },
    ref,
  ) => {
    const [t] = useTranslation();
    const isPalette = isColorPalette(color ?? '');
    const palette = useThemeColorPalette(isPalette ? (color! as ThemeColors) : 'primary');

    const backgroundLume = useMemo(() => {
      if (isPalette || !color || color.length === 0) {
        return null;
      }
      return chroma(color as string).luminance();
    }, [color]);

    const element = useMemo(() => {
      if (!onClick) {
        return 'div';
      }
      return 'button';
    }, [onClick]);

    return createElement(
      element,
      {
        ...props,
        ref: ref,
        type: onClick ? 'button' : undefined,
        'aria-pressed': active,
        onClick: onClick,
        className: clsx(classes.root, props.className, 'tag-component', {
          [classes.active]: active,
          [classes.interactive]: !!onClick,
          [classes.hasPrefix]: prefix || active,
          [classes.hasSuffix]: suffix || onRemove,
          [classes.dark]: backgroundLume !== null ? backgroundLume < 0.5 : undefined,
        }),
        style: {
          '--tag--background': isPalette ? palette?.main : color,
          '--tag--text-color': isPalette ? palette?.contrast : undefined,
        } as CSSProperties,
        disabled: disabled,
      },
      <>
        {(prefix || active)
          && (
            <div className={classes.prefix}>
              {active ? <Icon icon={['far', 'check']} /> : prefix}
            </div>
          )}
        <Typography variant="caption" className={classes.label} noWrap>
          {children}
        </Typography>
        {(suffix || onRemove) && (
          <div className={classes.suffix}>
            {suffix}
            {onRemove && (
              <IconButton
                label={t('common.delete')}
                variant="text"
                data-testid={'tag-remove'}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  e.bubbles = false;
                  onRemove?.();
                }}
              >
                <Icon icon={['far', 'times']} />
              </IconButton>
            )}
          </div>
        )}
      </>
    );
  },
);